/**
 * equationSvg.js — render an equation containing Unicode fraction slashes (⁄)
 * to a clean SVG and then to a small, crisp PNG blob suitable for pasting into
 * iMessage, Word, Notion, Gmail, etc.
 *
 * We do NOT use html2canvas / KaTeX HTML output — those produce broken visuals
 * (fraction bars overshoot, lines slice through glyphs). Instead we lay out the
 * equation ourselves with measured text widths and emit one tiny SVG.
 *
 * Supports:
 *   - inline text + super/subscripts (the Unicode source already uses ⁰¹² etc.)
 *   - stacked fractions via the U+2044 fraction slash, with balanced () groups
 *     (e.g. "(a+b)⁄(c-d)" stacks "a+b" over "c-d").
 *   - visual tensor-style sub/sup: ^(x) renders as a true superscript,
 *     _(x) renders as a true subscript. Strips the parens for cleanliness.
 *
 * Public API:
 *   renderEquationSvg(text, opts)  -> { svg, width, height }
 *   svgToPngBlob(svgString, width, height, scale=3) -> Promise<Blob>
 */

// Use just one font name (no commas, no quotes) so the SVG attribute parses
// reliably across browsers when loaded via <img src="data:..."> for export.
const FONT_FAMILY = "serif";

const escapeXml = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&apos;",
  }[c]));

// Walk the source string and split into a run of segments:
//   { t: "txt", v: "x = " }      — plain text
//   { t: "frac", n, d }          — stacked fraction (U+2044 source)
//   { t: "sup", v }              — superscript (was `^(...)` in source)
//   { t: "sub", v }              — subscript   (was `_(...)` in source)
// Order matters: scan left to right.
function parseParts(s) {
  const out = [];
  const isBreak = (c) => !c || /[\s+\-=<>,;]/.test(c);

  const scanNumStart = (end) => {
    if (end <= 0) return 0;
    if (s[end - 1] === ")") {
      let depth = 1;
      for (let k = end - 2; k >= 0; k--) {
        if (s[k] === ")") depth++;
        else if (s[k] === "(") { depth--; if (depth === 0) return k; }
      }
    }
    let k = end;
    while (k > 0 && !isBreak(s[k - 1])) k--;
    return k;
  };
  const scanDenEnd = (start) => {
    if (start >= s.length) return s.length;
    if (s[start] === "(") {
      let depth = 1;
      for (let k = start + 1; k < s.length; k++) {
        if (s[k] === "(") depth++;
        else if (s[k] === ")") { depth--; if (depth === 0) return k + 1; }
      }
    }
    let k = start;
    while (k < s.length && !isBreak(s[k])) k++;
    return k;
  };
  const unwrap = (str) => {
    if (str.length >= 2 && str[0] === "(" && str[str.length - 1] === ")") {
      let depth = 0;
      for (let k = 0; k < str.length; k++) {
        if (str[k] === "(") depth++;
        else if (str[k] === ")") {
          depth--;
          if (depth === 0 && k < str.length - 1) return str;
        }
      }
      return str.slice(1, -1);
    }
    return str;
  };

  // Pull a balanced `(...)` group starting at `start` (must point at `(`).
  // Returns [inner, indexAfterClosingParen] or null on mismatch.
  const grabParen = (start) => {
    if (s[start] !== "(") return null;
    let depth = 1;
    for (let k = start + 1; k < s.length; k++) {
      if (s[k] === "(") depth++;
      else if (s[k] === ")") {
        depth--;
        if (depth === 0) return [s.slice(start + 1, k), k + 1];
      }
    }
    return null;
  };

  // Helper to flush a plain-text run to the output.
  const flushTxt = (chunk) => {
    if (!chunk) return;
    // The chunk may still contain ^x / _x bare single-char super/sub. Rather than
    // re-tokenize here we just emit the raw text — the only case that matters
    // visually is the `^(...)` / `_(...)` paren-grouped form, which is split
    // before flushing.
    out.push({ t: "txt", v: chunk });
  };

  let i = 0;
  let buf = "";
  while (i < s.length) {
    const c = s[i];
    // Stacked fraction (highest priority since it spans both sides)
    if (c === "\u2044") {
      // Roll back to find numerator inside `buf` (already-accumulated text)
      const nStartInBuf = scanNumStart(buf.length);
      const num = unwrap(buf.slice(nStartInBuf));
      const prefix = buf.slice(0, nStartInBuf);
      const dEnd = scanDenEnd(i + 1);
      const den = unwrap(s.slice(i + 1, dEnd));
      flushTxt(prefix);
      if (num && den) out.push({ t: "frac", n: num, d: den });
      else flushTxt(buf.slice(nStartInBuf) + s.slice(i, dEnd));
      buf = "";
      i = dEnd;
      continue;
    }
    // ^(...) → visual superscript
    if (c === "^" && s[i + 1] === "(") {
      const p = grabParen(i + 1);
      if (p) {
        flushTxt(buf); buf = "";
        out.push({ t: "sup", v: p[0] });
        i = p[1];
        continue;
      }
    }
    // _(...) → visual subscript
    if (c === "_" && s[i + 1] === "(") {
      const p = grabParen(i + 1);
      if (p) {
        flushTxt(buf); buf = "";
        out.push({ t: "sub", v: p[0] });
        i = p[1];
        continue;
      }
    }
    buf += c;
    i++;
  }
  flushTxt(buf);
  return out;
}

let _measureCtx = null;
function measure(text, font) {
  if (!_measureCtx) _measureCtx = document.createElement("canvas").getContext("2d");
  _measureCtx.font = font;
  return _measureCtx.measureText(text).width;
}

export function renderEquationSvg(text, opts = {}) {
  const fontSize = opts.fontSize || 28;
  const smallSize = Math.round(fontSize * 0.7);
  const tinySize = Math.round(fontSize * 0.62); // tensor index size
  const padding = opts.padding ?? 14;
  const color = opts.color || "#000000";
  const bg = opts.bg || "#ffffff";
  const barThickness = Math.max(2, Math.round(fontSize / 14));

  const mainFont = `${fontSize}px ${FONT_FAMILY}`;
  const smallFont = `${smallSize}px ${FONT_FAMILY}`;
  const tinyFont = `${tinySize}px ${FONT_FAMILY}`;

  const parts = parseParts(text);

  // Measure each part
  const measured = parts.map((p) => {
    if (p.t === "txt") return { ...p, w: measure(p.v, mainFont) };
    if (p.t === "sup" || p.t === "sub") return { ...p, w: measure(p.v, tinyFont) + 2 };
    // fraction
    const wN = measure(p.n, smallFont);
    const wD = measure(p.d, smallFont);
    return { ...p, w: Math.max(wN, wD) + 12, wN, wD };
  });

  // Layout: vertical center anchored at centerY. Fractions stretch above and
  // below. Tensor indices sit slightly above/below the baseline.
  const fracHeight = smallSize * 2.1 + barThickness;
  const lineHeight = Math.max(fontSize * 1.2, fracHeight);
  const centerY = padding + lineHeight / 2;
  const totalW = padding * 2 + measured.reduce((s, p) => s + p.w, 0);
  const totalH = padding * 2 + lineHeight;

  let x = padding;
  const svgParts = [];
  for (const p of measured) {
    if (p.t === "txt") {
      svgParts.push(
        `<text x="${x.toFixed(2)}" y="${centerY.toFixed(2)}" font-family="${FONT_FAMILY}" font-size="${fontSize}" fill="${color}" dominant-baseline="middle" xml:space="preserve">${escapeXml(p.v)}</text>`
      );
    } else if (p.t === "sup") {
      // Lift baseline ~50% of fontSize above centerY
      const supY = centerY - fontSize * 0.35;
      svgParts.push(
        `<text x="${x.toFixed(2)}" y="${supY.toFixed(2)}" font-family="${FONT_FAMILY}" font-size="${tinySize}" fill="${color}" dominant-baseline="middle" xml:space="preserve">${escapeXml(p.v)}</text>`
      );
    } else if (p.t === "sub") {
      const subY = centerY + fontSize * 0.30;
      svgParts.push(
        `<text x="${x.toFixed(2)}" y="${subY.toFixed(2)}" font-family="${FONT_FAMILY}" font-size="${tinySize}" fill="${color}" dominant-baseline="middle" xml:space="preserve">${escapeXml(p.v)}</text>`
      );
    } else {
      // Fraction
      const cx = x + p.w / 2;
      const barY = centerY;
      const barW = p.w - 6;
      // Baseline-style vertical placement so the line sits naturally between
      // numerator and denominator.
      const numY = barY - smallSize * 0.35;
      const denY = barY + smallSize * 0.95;
      // Use a <rect> instead of <line> — universally honored when the SVG is
      // loaded inside an <img> for PNG export across browsers.
      svgParts.push(
        `<text x="${cx.toFixed(2)}" y="${numY.toFixed(2)}" font-family="${FONT_FAMILY}" font-size="${smallSize}" fill="${color}" text-anchor="middle" dominant-baseline="middle" xml:space="preserve">${escapeXml(p.n)}</text>`,
        `<rect x="${(cx - barW / 2).toFixed(2)}" y="${(barY - barThickness / 2).toFixed(2)}" width="${barW.toFixed(2)}" height="${barThickness}" fill="${color}" />`,
        `<text x="${cx.toFixed(2)}" y="${denY.toFixed(2)}" font-family="${FONT_FAMILY}" font-size="${smallSize}" fill="${color}" text-anchor="middle" dominant-baseline="middle" xml:space="preserve">${escapeXml(p.d)}</text>`,
      );
    }
    x += p.w;
  }

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${totalW}" height="${totalH}" viewBox="0 0 ${totalW} ${totalH}">` +
              `<rect width="${totalW}" height="${totalH}" fill="${bg}" />` +
              svgParts.join("") +
              `</svg>`;
  return { svg, width: totalW, height: totalH };
}

/**
 * Rasterize an SVG string to a PNG Blob. Uses an off-screen `<img>` and a 2D
 * canvas — fast, native, no third-party dependencies.
 */
export function svgToPngBlob(svgString, width, height, scale = 3) {
  return new Promise((resolve, reject) => {
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, h);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((b) => {
        URL.revokeObjectURL(url);
        if (b) resolve(b);
        else reject(new Error("Canvas toBlob returned null"));
      }, "image/png");
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}
